import axios from 'axios';
import { createApiRoot, attachUrugandaInterceptors } from '@uruganda/http';
import { resolveAgoraInitiativeId } from '../utils/agoraInitiative';
import { resolveLoginPath } from '../utils/portalAdminNav';

const APP_ID = (process.env.REACT_APP_APP_ID || 'entrea').trim().toLowerCase();

/** Une seule logique pour AuthContext + clients axios (évite :8002 en prod HTTPS). */
export function resolveRuntimeBackendUrl() {
  if (typeof window === 'undefined') return '';
  const pageHost = String(window.location.hostname || '').trim().toLowerCase();
  const pageIsLocalhost = pageHost === 'localhost' || pageHost === '127.0.0.1';
  const pageIsLan =
    !pageIsLocalhost &&
    pageHost &&
    pageHost !== 'uruganda-platform.test' &&
    !pageHost.endsWith('.uruganda-platform.test');

  try {
    if (pageHost === 'uruganda-platform.test' || pageHost.endsWith('.uruganda-platform.test')) {
      return '';
    }
  } catch {
    // no-op
  }
  const qsValue = (() => {
    try {
      return String(new URLSearchParams(window.location.search || '').get('backend_url') || '').trim();
    } catch {
      return '';
    }
  })();
  const storedValue = String(
    window.localStorage?.getItem('uruganda_backend_url')
      || window.localStorage?.getItem('platform_backend_public_url')
      || ''
  ).trim();
  const envValue = String(process.env.REACT_APP_BACKEND_URL || '').trim();

  // Query string explicite gagne toujours.
  if (qsValue) return qsValue;

  // Dev LAN (ex. http://192.168.0.110:3000) : utiliser le proxy webpack `/api`
  // plutôt que d'appeler :8002 depuis le téléphone (souvent bloqué par le pare-feu).
  if (pageIsLan && window.location.protocol === 'http:') {
    try {
      const stale = storedValue;
      if (stale && /:8002(\/|$)/.test(stale)) {
        window.localStorage?.removeItem('uruganda_backend_url');
        window.localStorage?.removeItem('platform_backend_public_url');
      }
    } catch {
      // no-op
    }
    return '';
  }

  let selected = String(storedValue || '').trim();
  // Ne jamais utiliser :8002 sur le même hôte en HTTPS (rien n’écoute en public sur ce port).
  if (selected && window.location.protocol === 'https:') {
    try {
      const u = new URL(selected, window.location.origin);
      if (
        u.hostname === window.location.hostname &&
        (u.port === '8002' || /:8002(\/|$)/.test(selected))
      ) {
        selected = '';
        try {
          window.localStorage?.removeItem('uruganda_backend_url');
          window.localStorage?.removeItem('platform_backend_public_url');
        } catch {
          // no-op
        }
      }
    } catch {
      // no-op
    }
  }
  if (selected) return selected;

  if (envValue && window.location.protocol === 'https:') {
    try {
      const u = new URL(envValue, window.location.origin);
      if (u.hostname === window.location.hostname && u.port === '8002') {
        return '';
      }
    } catch {
      // no-op
    }
  }

  return envValue;
}

const API_ROOT = createApiRoot(resolveRuntimeBackendUrl());
export const PLATFORM_V1 = `${API_ROOT}/v1`;
const API = `${API_ROOT}/apps/${APP_ID}`;

const portalAwareLoginPath = () =>
  typeof window !== 'undefined' ? resolveLoginPath(window.location.pathname) : '/login';

/** Routes `/api/v1/*` sans préfixe `/apps/{app_id}` (IAM plateforme, etc.). */
const platformHttp = axios.create({ baseURL: API_ROOT });
attachUrugandaInterceptors(platformHttp, { includeOrgHeaders: false, clearProjectOn401: false });

/** Même préfixe que PLATFORM_V1 — ne pas utiliser `axios` global (baseURL = /api/apps/entrea). */
const platformV1Http = axios.create({ baseURL: `${API_ROOT}/v1` });
attachUrugandaInterceptors(platformV1Http, {
  includeOrgHeaders: true,
  clearProjectOn401: true,
  loginPath: portalAwareLoginPath,
});

export const iamPlatformAPI = {
  loginHint: (username, appId = APP_ID) =>
    platformHttp.get('/v1/iam/login-hint', {
      params: {
        username: String(username || '').trim(),
        app_id: String(appId || APP_ID || 'entrea').trim() || 'entrea',
      },
    }),
};

// Configure axios (routes app Entrea sous /api/apps/{app_id})
axios.defaults.baseURL = API;
attachUrugandaInterceptors(axios, {
  includeOrgHeaders: true,
  clearProjectOn401: true,
  loginPath: portalAwareLoginPath,
});

/** Plateforme `/api/v1/people/*` (fiches People Core, hors routes app Entrea) */
export const peoplePlatformAPI = {
  list: (params = {}) => platformV1Http.get(`/people/people`, { params }),
  getProfileTypes: () => platformV1Http.get(`/people/people/profile-types`),
  getStatsSummary: (params = {}) =>
    platformV1Http.get(`/people/people/stats/summary`, { params }),
  getById: (id) => platformV1Http.get(`/people/people/${id}`),
  create: (data, params = {}) =>
    platformV1Http.post(`/people/people`, data, { params }),
  update: (id, data, params = {}) =>
    platformV1Http.put(`/people/people/${id}`, data, { params }),
  getHistory: (id, params = {}) =>
    platformV1Http.get(`/people/people/${id}/history`, { params }),
  deactivate: (id) => platformV1Http.post(`/people/people/${id}/deactivate`),
  reactivate: (id) => platformV1Http.post(`/people/people/${id}/reactivate`),
  submitForReview: (id) =>
    platformV1Http.post(`/people/people/${id}/submit-for-review`),
  getWorkflow: (id) => platformV1Http.get(`/people/people/${id}/workflow`),
  refreshFromWorkflow: (id) =>
    platformV1Http.post(`/people/people/${id}/refresh-from-workflow`),
};

/** Plateforme `/api/v1/ledger/*` — moteur transactionnel Ledgera (hors logique métier Entrea) */
export const ledgerPlatformAPI = {
  getMeta: () => platformV1Http.get(`/ledger/meta`),
  getOrgBalance: () => platformV1Http.get(`/ledger/balances/organization`),
  getStatsSummary: (params = {}) =>
    platformV1Http.get(`/ledger/stats/summary`, { params }),
  listTransactions: (params = {}) =>
    platformV1Http.get(`/ledger/transactions`, { params }),
  createTransaction: (data) => platformV1Http.post(`/ledger/transactions`, data),
  approveTransaction: (id) => platformV1Http.post(`/ledger/transactions/${id}/approve`),
  rejectTransaction: (id, reason) =>
    platformV1Http.post(`/ledger/transactions/${id}/reject`, { reason }),
  listAccounts: (params = {}) => platformV1Http.get(`/ledger/accounts`, { params }),
  createAccount: (data) => platformV1Http.post(`/ledger/accounts`, data),
  patchAccount: (id, data) => platformV1Http.patch(`/ledger/accounts/${id}`, data),
  listBankDetails: (params = {}) => platformV1Http.get(`/ledger/bank-details`, { params }),
  createBankDetails: (data) => platformV1Http.post(`/ledger/bank-details`, data),
  patchBankDetails: (id, data) => platformV1Http.patch(`/ledger/bank-details/${id}`, data),
  deleteBankDetails: (id) => platformV1Http.delete(`/ledger/bank-details/${id}`),
  listPaymentMethods: (params = {}) =>
    platformV1Http.get(`/ledger/payment-methods`, { params }),
  createPaymentMethod: (data) => platformV1Http.post(`/ledger/payment-methods`, data),
  patchPaymentMethod: (id, data) => platformV1Http.patch(`/ledger/payment-methods/${id}`, data),
  deactivatePaymentMethod: (id) =>
    platformV1Http.post(`/ledger/payment-methods/${id}/deactivate`),
  listJournals: (params = {}) => platformV1Http.get(`/ledger/journals`, { params }),
};

/** Alias historique utilisé par plusieurs pages Entrea. */
export const ledgerApi = ledgerPlatformAPI;

/** Plateforme `/api/v1/agora/*` — activités/initiatives transverses */
export const agoraPlatformAPI = {
  listActivities: (params = {}) =>
    platformV1Http.get(`/agora/activities`, { params }),
  listInitiatives: (params = {}) =>
    platformV1Http.get(`/agora/initiatives`, { params }),
};

/** Plateforme `/api/v1/messaging/*` — inbox notifications transverses */
export const messagingPlatformAPI = {
  listNotifications: (params = {}) =>
    platformV1Http.get(`/messaging/notifications`, { params }),
  markNotificationRead: (id) => platformV1Http.post(`/messaging/notifications/${id}/read`),
};

/** Plateforme `/api/v1/files/*` — stockage fichiers Core (upload / métadonnées) */
export const filesPlatformAPI = {
  list: (params = {}) => platformV1Http.get(`/files`, { params }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('upload', file);
    return platformV1Http.post(`/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (fileId) => platformV1Http.get(`/files/${fileId}`),
  delete: (fileId) => platformV1Http.delete(`/files/${fileId}`),
};

/** Plateforme `/api/v1/workflow/*` — workflows génériques (approbation / rejet) */
export const workflowPlatformAPI = {
  create: (data) => platformV1Http.post(`/workflow/workflows`, data),
  get: (workflowId) => platformV1Http.get(`/workflow/workflows/${workflowId}`),
  approve: (workflowId, comment) =>
    platformV1Http.post(`/workflow/workflows/${workflowId}/approve`, { comment: comment ?? null }),
  reject: (workflowId, comment) =>
    platformV1Http.post(`/workflow/workflows/${workflowId}/reject`, { comment: comment ?? null }),
  history: (workflowId) =>
    platformV1Http.get(`/workflow/workflows/${workflowId}/history`),
};

/** Plateforme `/api/v1/presence/*` */
export const presencePlatformAPI = {
  listProfiles: () => platformV1Http.get(`/presence/profiles`),
};

// Members API
export const membersAPI = {
  getAll: (params = {}) => axios.get('/members', { params }),
  getById: (id) => axios.get(`/members/${id}`),
  create: (data) => axios.post('/members', data),
  update: (id, data) => axios.put(`/members/${id}`, data),
  delete: (id) => axios.delete(`/members/${id}`),
  hardDelete: (id) => axios.delete(`/members/${id}`, { params: { hard: true } }),
  deactivate: (id) => axios.put(`/members/${id}/deactivate`),
  bulkUpdate: (ids, data) => axios.post('/members/bulk-update', { member_ids: ids, ...data }),
  bulkDelete: (ids) => axios.delete('/members/bulk-delete', { data: { member_ids: ids } }),
  bulkHardDelete: (ids) => axios.delete('/members/bulk-delete', { params: { hard: true }, data: { member_ids: ids } }),
  getContributions: (id) => axios.get(`/members/${id}/contributions`),
  getStats: (id) => axios.get(`/members/${id}/stats`)
};

// IAM Groups API (Core)
export const iamGroupsAPI = {
  getAll: (params = {}) => axios.get('/groups', { params }),
  getById: (id) => axios.get(`/groups/${id}`),
  create: (data) => axios.post('/groups', data),
  update: (id, data) => axios.put(`/groups/${id}`, data),
  delete: (id) => axios.delete(`/groups/${id}`),
};

// Procès-verbaux : rapports Agora `/api/v1/agora/initiatives/{initiativeId}/reports` (plus de proxy Entrea `/meetings`).
function _lines(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) return val.split('\n').map((s) => s.trim()).filter(Boolean);
  return [];
}

function reportToMeeting(r) {
  if (!r) return null;
  return {
    id: r.id,
    title: r.title || (r.summary && String(r.summary).slice(0, 200)) || 'Procès-verbal',
    date: r.date,
    location: r.location,
    participants: r.participants || [],
    agenda: _lines(r.agenda),
    decisions: _lines(r.decisions),
    event_id: r.activity_id || null,
    is_published: r.is_published ?? false,
    pending_approval: r.pending_approval ?? false,
  };
}

function buildReportPayload(data) {
  const title = (data.title || '').trim() || 'Procès-verbal';
  const agenda = Array.isArray(data.agenda)
    ? data.agenda
    : String(data.agenda || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
  const decisions = Array.isArray(data.decisions)
    ? data.decisions
    : String(data.decisions || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
  return {
    summary: title,
    outcome: decisions.length ? decisions.join('\n') : null,
    title,
    date: data.date || null,
    location: data.location || null,
    participants: Array.isArray(data.participants) ? data.participants : [],
    agenda,
    decisions,
    activity_id: data.event_id || null,
  };
}

export const meetingsAPI = {
  /** Liste : rapports Agora pour l'initiative du projet courant */
  getAll: async (params = {}) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const skip = params.skip ?? 0;
    const limit = params.limit ?? 500;
    const res = await platformV1Http.get(`/agora/initiatives/${initiativeId}/reports`, {
      params: { skip, limit },
    });
    const reports = Array.isArray(res.data) ? res.data : [];
    const meetings = reports.map((row) => reportToMeeting(row));
    return { data: { meetings, total: meetings.length } };
  },
  getById: async (id) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const res = await platformV1Http.get(`/agora/initiatives/${initiativeId}/reports/${id}`);
    return { data: reportToMeeting(res.data) };
  },
  create: async (data) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const body = { ...buildReportPayload(data), pending_approval: false };
    const res = await platformV1Http.post(`/agora/initiatives/${initiativeId}/reports`, body);
    return { data: reportToMeeting(res.data) };
  },
  update: async (id, data) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const body = buildReportPayload(data);
    const res = await platformV1Http.put(`/agora/initiatives/${initiativeId}/reports/${id}`, body);
    return { data: reportToMeeting(res.data) };
  },
  delete: async (id) => {
    const initiativeId = await resolveAgoraInitiativeId();
    await platformV1Http.delete(`/agora/initiatives/${initiativeId}/reports/${id}`);
    return { data: { deleted: true } };
  },
  publish: async (id) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const res = await platformV1Http.post(`/agora/initiatives/${initiativeId}/reports/${id}/publish`);
    return { data: reportToMeeting(res.data) };
  },
  submitForApproval: async (id) => {
    const initiativeId = await resolveAgoraInitiativeId();
    const res = await platformV1Http.put(`/agora/initiatives/${initiativeId}/reports/${id}`, {
      pending_approval: true,
    });
    return { data: reportToMeeting(res.data) };
  },
  approve: async (id) => {
    const initiativeId = await resolveAgoraInitiativeId();
    await platformV1Http.post(`/agora/initiatives/${initiativeId}/reports/${id}/approve`);
    const pub = await platformV1Http.post(`/agora/initiatives/${initiativeId}/reports/${id}/publish`);
    return { data: reportToMeeting(pub.data) };
  },
};

// Financial API
export const financialAPI = {
  getTransactions: (params = {}) => axios.get('/financial/transactions', { params }),
  getTransaction: (id) => axios.get(`/financial/transactions/${id}`),
  createTransaction: (data) => axios.post('/financial/transactions', data),
  updateTransaction: (id, data) => axios.put(`/financial/transactions/${id}`, data),
  deleteTransaction: (id) => axios.delete(`/financial/transactions/${id}`),
  approveTransaction: (id) => axios.put(`/financial/transactions/${id}/approve`),
  getStats: () => axios.get('/financial/stats')
};

// Documents API
export const documentsAPI = {
  getAll: (params = {}) => axios.get('/documents', { params }),
  create: (data) => axios.post('/documents', data),
  update: (id, data) => axios.put(`/documents/${id}`, data),
  delete: (id) => axios.delete(`/documents/${id}`)
};

/** Normalise une ligne Agora (activité workspace ou événement public) pour le calendrier Entrea (`start_date`, `type`). */
function toCalendarDateInput(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const head = value.includes('T') ? value.split('T')[0] : value;
    return head.slice(0, 10);
  }
  return '';
}

function calendarTypeFromActivityKind(row) {
  const k = String(row.activity_kind || '').toLowerCase();
  if (k === 'meeting') return 'meeting';
  if (k === 'milestone' || k === 'deadline') return 'deadline';
  return 'event';
}

function normalizeAgoraRowForCalendar(row, source) {
  const dateSrc = row.planned_date ?? row.scheduled_at ?? row.actual_date ?? row.created_at;
  const start_date = toCalendarDateInput(dateSrc);
  const calendarType =
    source === 'public_event' ? 'event' : calendarTypeFromActivityKind(row);
  return {
    ...row,
    start_date,
    end_date: row.end_date ? toCalendarDateInput(row.end_date) : row.end_date || '',
    type: ['meeting', 'deadline', 'event'].includes(row.type) ? row.type : calendarType,
    _calendarSource: source === 'public_event' ? 'event' : 'activity',
  };
}

// Events/Calendar API
export const eventsAPI = {
  getAll: async (params = {}) => {
    const mergedParams = { limit: 500, ...params };
    const [activitiesRes, eventsRes] = await Promise.all([
      platformV1Http.get(`/agora/activities`, { params: mergedParams }),
      platformV1Http.get(`/agora/events`, { params: mergedParams }),
    ]);
    const activities = activitiesRes.data?.activities || [];
    const events = eventsRes.data?.events || [];
    const merged = [
      ...activities.map((a) => normalizeAgoraRowForCalendar(a, 'activity')),
      ...events.map((e) => normalizeAgoraRowForCalendar(e, 'public_event')),
    ];
    merged.sort((a, b) => {
      const da = a.start_date || '9999-12-31';
      const db = b.start_date || '9999-12-31';
      if (da !== db) return da.localeCompare(db);
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
    return { data: { events: merged } };
  },
  getById: (id) => platformV1Http.get(`/agora/events/${id}`),
  getActivityById: (id) => platformV1Http.get(`/agora/activities/${id}`),
  getPublicById: (id) => platformV1Http.get(`/agora/public/events/${id}`),
  listPublic: (params = {}) => platformV1Http.get(`/agora/public/events`, { params }),
  create: (data) => platformV1Http.post(`/agora/events`, data),
  createActivity: async (data, initiativeId) => {
    const resolvedInitiativeId = initiativeId || (await resolveAgoraInitiativeId());
    return platformV1Http.post(`/agora/activities`, {
      ...data,
      initiative_id: resolvedInitiativeId,
    });
  },
  update: (id, data) => platformV1Http.put(`/agora/events/${id}`, data),
  updateActivity: async (_initiativeId, activityId, data) =>
    platformV1Http.put(`/agora/activities/${activityId}`, data),
  delete: (id) => platformV1Http.delete(`/agora/events/${id}`),
  deleteActivity: async (_initiativeId, activityId) =>
    platformV1Http.delete(`/agora/activities/${activityId}`),
  uploadPoster: async (id, file) => {
    const uploaded = await filesPlatformAPI.upload(file);
    const fileUrl = uploaded?.data?.url || uploaded?.data?.download_url || uploaded?.data?.file_url || '';
    if (!fileUrl) {
      throw new Error('Upload affiche: URL fichier introuvable');
    }
    await platformV1Http.put(`/agora/events/${id}`, { poster_url: fileUrl });
    return { data: { poster_url: fileUrl } };
  },
  register: (id, data) => platformV1Http.post(`/agora/activities/${id}/registrations`, data),
  registerPublic: (id, data) => platformV1Http.post(`/agora/public/events/${id}/registrations`, data),
  uploadPaymentProof: async (_id, file) => {
    const uploaded = await filesPlatformAPI.upload(file);
    const fileUrl = uploaded?.data?.url || uploaded?.data?.download_url || uploaded?.data?.file_url || '';
    if (!fileUrl) {
      throw new Error('Upload preuve: URL fichier introuvable');
    }
    return { data: { payment_proof_url: fileUrl } };
  },
  getRegistrations: (id, params = {}) =>
    platformV1Http.get(`/agora/activities/${id}/registrations`, { params }),
  updateRegistration: (eventId, registrationId, data) =>
    platformV1Http.put(`/agora/activities/${eventId}/registrations/${registrationId}`, data),
  sendConfirmation: (eventId, registrationId) =>
    platformV1Http.put(`/agora/activities/${eventId}/registrations/${registrationId}`, {
      confirmation_sent: true,
    }),
  verifyCode: (eventId, registrationId, code) =>
    platformV1Http.get(`/agora/activities/${eventId}/registrations/${registrationId}`)
};

// Notifications API
export const notificationsAPI = {
  getAll: () => axios.get('/notifications'),
  create: (data) => axios.post('/notifications', data),
  markAsRead: (id) => axios.put(`/notifications/${id}/read`)
};

// Contributions API
export const contributionsAPI = {
  getByPerson: (personId) => axios.get(`/contributions/persons/${personId}`),
  getMemberContributions: (memberId) => axios.get(`/members/${memberId}/contributions`),
  getMemberStats: (memberId) => axios.get(`/members/${memberId}/stats`),
  getAll: (params = {}) => axios.get('/contributions', { params }),
  getStatsSummary: () => axios.get('/contributions/stats/summary'),
  create: (data) => axios.post('/contributions', data),
  update: (id, data) => axios.put(`/contributions/${id}`, data),
  approve: (id) => axios.put(`/contributions/${id}/approve`),
  delete: (id) => axios.delete(`/contributions/${id}`),
  bulkCreate: (data) => axios.post('/contributions/bulk-create', data),
  uploadFiles: async (files) => {
    const resolveUploadedFileUrl = (payload) => {
      if (!payload || typeof payload !== 'object') return '';
      const directUrl =
        payload.url ||
        payload.download_url ||
        payload.file_url ||
        payload.public_url ||
        payload.path ||
        '';
      if (directUrl) return String(directUrl);
      const fileId = payload.id || payload.file_id || payload.upload_id || '';
      if (fileId) {
        // Fallback stable: endpoint fichier plate-forme par identifiant.
        return `${PLATFORM_V1}/files/${String(fileId)}`;
      }
      return '';
    };

    const uploadedUrls = [];
    for (const file of files || []) {
      const uploaded = await filesPlatformAPI.upload(file);
      const fileUrl = resolveUploadedFileUrl(uploaded?.data);
      if (!fileUrl) {
        throw new Error('Upload justificatif: URL fichier introuvable');
      }
      uploadedUrls.push(fileUrl);
    }
    return { data: { files: uploadedUrls } };
  }
};

// Assistance API (mecanismes + dossiers)
export const assistanceAPI = {
  // Mechanisms
  getMechanisms: (params = {}) => axios.get('/solidarity-mechanisms', { params }),
  createMechanism: (data) => axios.post('/solidarity-mechanisms', data),
  getMechanismById: (id) => axios.get(`/solidarity-mechanisms/${id}`),
  updateMechanism: (id, data) => axios.put(`/solidarity-mechanisms/${id}`, data),
  patchMechanism: (id, data) => axios.patch(`/solidarity-mechanisms/${id}`, data),
  deleteMechanism: (id) => axios.delete(`/solidarity-mechanisms/${id}`),
  setMechanismStatus: (id, status) =>
    axios.patch(`/solidarity-mechanisms/${id}/status`, { status }),

  // Cases
  getCases: (params = {}) => axios.get('/assistance-cases', { params }),
  createCase: (data) => axios.post('/assistance-cases', data),
  getCaseById: (id) => axios.get(`/assistance-cases/${id}`),
  updateCase: (id, data) => axios.patch(`/assistance-cases/${id}`, data),
  deleteCase: (id) => axios.delete(`/assistance-cases/${id}`),
  attachFiles: (id, supporting_file_ids) =>
    axios.post(`/assistance-cases/${id}/attach-files`, { supporting_file_ids }),
  submitCase: (id) => axios.post(`/assistance-cases/${id}/submit`),
  approveCase: (id, data) => axios.post(`/assistance-cases/${id}/approve`, data),
  rejectCase: (id, data) => axios.post(`/assistance-cases/${id}/reject`, data),
  markPaid: (id, data) => axios.post(`/assistance-cases/${id}/mark-paid`, data),
  closeCase: (id) => axios.post(`/assistance-cases/${id}/close`),

  // Reports
  getSummaryReport: () => axios.get('/assistance-reports/summary'),
  getMemberReport: (memberProfileId) =>
    axios.get(`/assistance-reports/member/${memberProfileId}`),
};

// Approval Requests API
export const approvalRequestsAPI = {
  getCount: async () => {
    try {
      const { data } = await platformV1Http.get(`/messaging/notifications`, {
        params: { unread_only: true, limit: 1, skip: 0 },
      });
      const messagingCount = Number(data?.total || 0);
      try {
        // Endpoint métier Entrea: cotisations en attente d'approbation.
        const pendingRes = await axios.get('/approval-requests/count');
        const pendingCount = Number(pendingRes?.data?.count || 0);
        return { data: { count: pendingCount > 0 ? pendingCount : messagingCount } };
      } catch (_pendingErr) {
        return { data: { count: messagingCount } };
      }
    } catch (_err) {
      const pendingRes = await axios.get('/approval-requests/count');
      return { data: { count: Number(pendingRes?.data?.count || 0) } };
    }
  },
  getAll: async (params = {}) => {
    const limit = Number(params?.limit || 20);
    const skip = Number(params?.skip || 0);
    const selectedStatus = String(params?.status || "").toLowerCase();
    const unreadOnly = selectedStatus === "pending" ? true : Boolean(params?.unread_only);
    try {
      const { data } = await platformV1Http.get(`/messaging/notifications`, {
        params: { limit, skip, unread_only: unreadOnly },
      });
      const notifications = Array.isArray(data?.notifications) ? data.notifications : [];
      const requests = notifications.map((notif) => ({
        id: notif.id,
        action: "update",
        resource_type: "notification",
        status: notif.read_at ? "approved" : "pending",
        data: {
          title: notif.title || "Notification",
          message: notif.message || "",
          link: notif.link || null,
        },
        reason: notif.message || "",
        created_at: notif.created_at,
        approved_at: notif.read_at || null,
        raw: notif,
      }));
      if (requests.length > 0 || Number(data?.total || 0) > 0) {
        return { data: { requests, total: Number(data?.total || requests.length) } };
      }
    } catch (_err) {
      // fallback ci-dessous
    }

    // Fallback métier: cotisations pending quand la messagerie est vide/inaccessible.
    const contributionsRes = await axios.get('/contributions', {
      params: { limit, skip, status: 'pending' },
    });
    const rows = Array.isArray(contributionsRes?.data)
      ? contributionsRes.data
      : (contributionsRes?.data?.contributions || []);
    const total = Number(contributionsRes?.data?.total || rows.length || 0);
    const requests = rows.map((c) => ({
      id: c.id,
      action: 'update',
      resource_type: 'contribution',
      status: 'pending',
      data: {
        title: 'Cotisation en attente',
        message: `Cotisation ${c?.month || ''} - ${Number(c?.amount || 0).toLocaleString('fr-FR')} FBU`,
        member_id: c?.person_id || c?.member_id || null,
        month: c?.month || null,
        amount: Number(c?.amount || 0),
      },
      reason: c?.notes || '',
      created_at: c?.created_at,
      approved_at: null,
      raw: c,
    }));
    return { data: { requests, total } };
  },
  create: (data) => axios.post('/approval-requests', data),
  approve: (id) => platformV1Http.post(`/messaging/notifications/${id}/read`),
  reject: (id) => platformV1Http.post(`/messaging/notifications/${id}/read`)
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => axios.get('/dashboard/stats'),
  getPersonalStats: () => axios.get('/dashboard/stats/personal')
};

// Settings API — réponse attendue depuis le proxy `/api/apps/entrea/settings` (pas de défauts inventés côté client)
export const settingsAPI = {
  getAll: () => axios.get('/settings'),
  update: (data) => axios.put('/settings', data),
  addBankAccount: (data) => axios.post('/settings/bank-accounts', data),
  updateBankAccount: (id, data) => axios.put(`/settings/bank-accounts/${id}`, data),
  deleteBankAccount: (id) => axios.delete(`/settings/bank-accounts/${id}`),
  addSignatory: (data) => axios.post('/settings/signatories', data),
  updateSignatory: (id, data) => axios.put(`/settings/signatories/${id}`, data),
  deleteSignatory: (id) => axios.delete(`/settings/signatories/${id}`)
};

// Modules API
export const modulesAPI = {
  getAll: () => axios.get('/modules'),
  updateModule: (moduleId, data) => axios.put(`/modules/${moduleId}`, data),
  updateComponent: (moduleId, componentId, data) => axios.put(`/modules/${moduleId}/components/${componentId}`, data)
};

// Organizations API (SaaS Multi-tenant)
export const organizationsAPI = {
  register: (data) => axios.post('/organizations/register', data),
  getMe: () => axios.get('/organizations/me'),
  updateMe: (data) => axios.put('/organizations/me', data),
  // SaaS Admin endpoints
  getAll: (params = {}) => axios.get('/organizations', { params }),
  getById: (id) => axios.get(`/organizations/${id}`),
  update: (id, data) => axios.put(`/organizations/${id}`, data),
  delete: (id) => axios.delete(`/organizations/${id}`),
  // Organization modules management (SaaS Admin only)
  getModules: (organizationId) => axios.get(`/organizations/${organizationId}/modules`),
  updateModule: (organizationId, moduleId, data) => axios.put(`/organizations/${organizationId}/modules/${moduleId}`, data),
  updateComponent: (organizationId, moduleId, componentId, data) => axios.put(`/organizations/${organizationId}/modules/${moduleId}/components/${componentId}`, data),
  
  // Apps & Projects
  getAvailableApps: () => axios.get('/apps'),
  getOrganizationApps: (organizationId) => axios.get(`/organizations/${organizationId}/apps`),
  activateApp: (organizationId, appId) => axios.post(`/organizations/${organizationId}/apps/${appId}/activate`),
  deactivateApp: (organizationId, appId) => axios.post(`/organizations/${organizationId}/apps/${appId}/deactivate`),
  getOrganizationProjects: (organizationId) => axios.get(`/organizations/${organizationId}/projects`),
  
  // Projects
  getProject: (projectId) => axios.get(`/projects/${projectId}`),
  updateProject: (projectId, data) => axios.put(`/projects/${projectId}`, data)
};

// Users API
export const usersAPI = {
  getAll: (params = {}) => axios.get('/users', { params }),
  getById: (id) => axios.get(`/users/${id}`),
  getByMemberId: (memberId) => axios.get(`/users/by-member/${memberId}`),
  create: (data) => axios.post('/users', data),
  update: (id, data) => axios.put(`/users/${id}`, data),
  /** Réinitialisation admin (un ou plusieurs comptes projet). */
  resetPasswords: (data) => axios.post('/users/reset-password', data),
  delete: (id) => axios.delete(`/users/${id}`),
  getAuditLogs: (id, params = {}) => axios.get(`/admin/users/${id}/audit-logs`, { params }),
  getSessions: (id, params = {}) => axios.get(`/admin/users/${id}/sessions`, { params }),
  getValidationInfo: (id) => axios.get(`/users/${id}/validation-info`)
};

// Roles API
export const rolesAPI = {
  /** Liste catalogue Entrea (proxy app) — inchangée pour compatibilité écrans membres. */
  getAll: () => axios.get('/roles'),
  /** Liste IAM plateforme (GET /api/v1/iam/roles). */
  getManaged: (params = {}) => platformV1Http.get(`/iam/roles`, { params }),
  getList: () => axios.get('/roles/list'),
  getById: (id) => platformV1Http.get(`/iam/roles/${encodeURIComponent(String(id))}`),
  create: (data) => platformV1Http.post(`/iam/roles`, data),
  update: (id, data) => platformV1Http.put(`/iam/roles/${encodeURIComponent(String(id))}`, data),
  delete: (id) => platformV1Http.delete(`/iam/roles/${encodeURIComponent(String(id))}`),
  getPermissions: () => axios.get('/roles/permissions'),
  getRolePermissions: (role) => axios.get(`/roles/${encodeURIComponent(String(role))}/permissions`)
};

/** Positions métier (projet) — définies dans la console admin. */
export const positionsAPI = {
  getList: () => axios.get('/positions'),
  create: (data) => axios.post('/positions', data),
  update: (id, data) => axios.put(`/positions/${encodeURIComponent(String(id))}`, data),
  delete: (id) => axios.delete(`/positions/${encodeURIComponent(String(id))}`),
};

// Permissions API
export const permissionsAPI = {
  getAll: () => axios.get('/permissions'),
  create: (data) => axios.post('/permissions', data),
  update: (id, data) => axios.put(`/permissions/${id}`, data),
  delete: (id) => axios.delete(`/permissions/${id}`)
};

// Admin API
export const adminAPI = {
  getStats: () => axios.get('/admin/stats'),
  getAuditLogs: (params) => axios.get('/admin/audit-logs', { params }),
  getActiveUsers: () => axios.get('/admin/users/active'),
  getActiveSessions: () => axios.get('/admin/sessions/active'),
  terminateUserSessions: (userId) =>
    axios.post('/admin/sessions/terminate', { user_id: userId }),
  getSystemInfo: () => axios.get('/admin/system/info'),
  getProjects: (params = {}) => axios.get('/admin/projects', { params }),
  getSubscriptions: (params = {}) => axios.get('/admin/subscriptions', { params }),
  getInvoices: (params = {}) => axios.get('/admin/billing/invoices', { params }),
  getOrganizationBillingTransactions: (organizationId, params = {}) =>
    axios.get(`/admin/billing/transactions/organizations/${organizationId}`, { params }),
  exportData: (resourceType, format = 'json') => axios.post('/admin/export/data', { resource_type: resourceType, format }),
  importData: (resourceType, data, format = 'json', extra = {}) =>
    axios.post('/admin/import/data', { resource_type: resourceType, format, data, ...(extra || {}) })
};

// Auth API (chemins sous /api/v1/iam — utiliser platformV1Http, pas axios + baseURL apps/entrea)
export const authAPI = {
  login: (username, password) =>
    platformV1Http.post('/iam/login', {
      username,
      password,
      audience: 'organization',
      app_id: APP_ID,
    }),
  changePassword: (oldPassword, newPassword, projectId) =>
    platformV1Http.post('/iam/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
      app_id: APP_ID,
      project_id: projectId || localStorage.getItem('project_id') || undefined,
    }),
  register: (_userData) => Promise.reject(new Error('Direct register is disabled for IAM app mode')),
  getMe: () => platformV1Http.get('/iam/me'),
  logout: () => platformV1Http.post('/iam/logout'),
  // PIN Authentication
  verifyPhone: (phone) => axios.post('/auth/verify-phone', { phone }),
  verifyPin: (phone, pin) => axios.post('/auth/verify-pin', { phone, pin }),
  createPin: (pin, oldPin = null) => axios.post('/auth/create-pin', { pin, old_pin: oldPin }),
  // Account Validation
  getValidateAccountInfo: (token) => axios.get(`/auth/validate-account/${token}`),
  validateAccount: (data) => axios.post('/auth/validate-account', data)
};

export default {
  peoplePlatform: peoplePlatformAPI,
  ledgerPlatform: ledgerPlatformAPI,
  agoraPlatform: agoraPlatformAPI,
  messagingPlatform: messagingPlatformAPI,
  filesPlatform: filesPlatformAPI,
  workflowPlatform: workflowPlatformAPI,
  presencePlatform: presencePlatformAPI,
  members: membersAPI,
  meetings: meetingsAPI,
  financial: financialAPI,
  documents: documentsAPI,
  events: eventsAPI,
  notifications: notificationsAPI,
  contributions: contributionsAPI,
  assistance: assistanceAPI,
  dashboard: dashboardAPI,
  settings: settingsAPI,
  users: usersAPI,
  iamGroups: iamGroupsAPI,
  roles: rolesAPI,
  positions: positionsAPI,
  permissions: permissionsAPI,
  admin: adminAPI
};
